import {useEffect} from 'react';

interface Use3DProps {
    map: mapboxgl.Map | undefined;
    is3D: boolean;
}

function setup3D(map: mapboxgl.Map) {
    // Allow opt + click drag to rotate the map when in 3D mode
    if (!map.dragRotate.isEnabled()) map.dragRotate.enable();
    map.touchZoomRotate.enableRotation();
    map.keyboard.enableRotation();
    map.setMinZoom(8);

    waitForMapToFinishUpdating(map)
        .then(() =>
            map.easeTo({
                pitch: 30,
                // Try to make sure we don't end up inside a mountain
                zoom: Math.min(map.getZoom(), 14),
            })
        )
        .catch(console.error);
}

function waitForMapToFinishUpdating(map: mapboxgl.Map): Promise<void> {
    return new Promise((resolve) => {
        // There is not really a hook into whether or not the map is done updating, so we have to
        // attach an interval that polls the map every 100ms to check if the style is done loading
        map.once('styledata', () => {
            const intervalId = setInterval(() => {
                if (!map.isStyleLoaded()) return;
                clearInterval(intervalId);
                resolve();
            }, 100);
        });
    });
}

function setup2D(map: mapboxgl.Map) {
    if (map.dragRotate.isEnabled()) map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    map.setMinZoom(undefined);

    // If this is called when the map is mounted it creates a race condition with the
    // the map state created when preview is true on <Map/>
    if (map.getPitch() !== 0 || map.getBearing() !== 0) {
        map.easeTo({pitch: 0, bearing: 0});
    }
}

export function use3D({map, is3D}: Use3DProps) {
    useEffect(() => {
        if (!map) return;

        if (is3D) setup3D(map);
        else setup2D(map);
    }, [map, is3D]);
}
